import { useState, useEffect, useContext } from 'react';
import ApiConstants from '../utils/ApiConstants';
import { useAuthenticatedApiFetch } from '../hooks/useAuthenticatedApiFetch';
import { useMsal } from "@azure/msal-react";
import { LanguageContext } from "../context/LanguageProvider";
import { formatCurrency } from '../utils/utils';

const Premium = ({
  question,
  setQuestions,
  inputBlurTriggered,
  client,
  product,
  handleInputBlur,
  inputRefs,
  validationErrors
}) => {
  const { instance } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const { t, language } = useContext(LanguageContext);

  const [premiums, setPremiums] = useState(null);
  const [apiError, setApiError] = useState(false);

  const getPaymentType = (uiValue) => {
    if (/Gold|Or/i.test(uiValue)) {
      return 'gold';
    } else if (/Platinum|Platine/i.test(uiValue)) {
      return 'platinum';
    }
    return null;
  };

  useEffect(() => {
    const fetchPremiums = async () => {
      try {
        await instance.initialize();

        const endpoint = ApiConstants.getPremiumCalculationEndpoint(product, client);
        const url = `${ApiConstants.BASE_URL}${endpoint}`;
        const result = await authenticatedApiFetch(url);

        if (result.status === 200) {
          setPremiums(result.data);
          setApiError(false);
        } else {
          setApiError(true);
        }
      } catch (error) {
        console.error("Error fetching premiums:", error);
        setApiError(true);
      }
    };

    const timeoutId = setTimeout(fetchPremiums, 100);

    return () => clearTimeout(timeoutId);
  }, [inputBlurTriggered]);



  const [premiumLessThanTwelve, setPremiumLessThanTwelve] = useState(false);
  const [premiumInvalid, setPremiumInvalid] = useState(false);

useEffect(() => {
  if (!premiums || premiums.platinumFirstPayment == null || premiums.goldFirstPayment == null) {
    setPremiumLessThanTwelve(false);
    setPremiumInvalid(false);
    return;
  }

  // Check for -1 error
  if (premiums.goldFirstPayment === -1 || premiums.platinumFirstPayment === -1) {
    setPremiumInvalid(true);
    setPremiumLessThanTwelve(false);
  } 
  // Check for < 12 case
  else if (premiums.goldFirstPayment < 12 || premiums.platinumFirstPayment < 12) {
    setPremiumLessThanTwelve(true);
    setPremiumInvalid(false);
  } 
  // Normal case
  else {
    setPremiumLessThanTwelve(false);
    setPremiumInvalid(false);
  }
}, [premiums]);


  const handleRadioChange = async (event, currentQuestion) => {
    const updatedQuestionValue = {
      ...currentQuestion,
      value: event.target.value,
    };

    await handleInputBlur(updatedQuestionValue);
  };

  const shouldShowBothButtons = premiums && premiums['goldFirstPayment'] !== null && premiums['platinumFirstPayment'] !== null;



  //console.log("is there a second value? : ", premiums ? premiums.platinumFirstPayment : null);


  const renderRadioButton = (valueIndex) => {
    const paymentTypeRaw = question.possibleValues[valueIndex]?.uiValue;
    const paymentType = getPaymentType(paymentTypeRaw);
    const firstPayment = premiums ? premiums[`${paymentType}FirstPayment`] : 0;
    const renewalPayment = premiums ? premiums[`${paymentType}RenewalPayment`] : 0;

    return (
      <>
        <input
          type="radio"
          id={`${question.uniqueId}__${question.possibleValues[valueIndex].value}`}
          name={question.uniqueId}
          value={question.possibleValues[valueIndex].value}
          checked={question.value === question.possibleValues[valueIndex].value}

          onChange={(e) => handleRadioChange(e, question)}
        />
        <label htmlFor={`${question.uniqueId}__${question.possibleValues[valueIndex].value}`} className="amounts">



          <div className="price">
            {typeof firstPayment === 'number' ? `${formatCurrency(firstPayment, language, false, 'MONEY-REAL')}` : 'N/A'}
          </div>

          <div className="level">
            {premiums && premiums['platinumFirstPayment'] === null ? t('renewal') : question.possibleValues[valueIndex].uiValue}
          </div>

          <div className="sum">
            {typeof renewalPayment === 'number' ? `${formatCurrency(renewalPayment, language, false, 'MONEY-REAL')}` : 'N/A'}
          </div>
        </label>
      </>
    );
  };

  return (
    <div className={`my-4 premium ${validationErrors && validationErrors.some((error) => error.id === question.id) ? "hasError" : ""}`}

    data-question-code={question.code}

      ref={(el) => {
        const refId = question.uniqueId;
        inputRefs.current[refId] = el;
      }}

    >
      <div className="pb-2">
        <label>{question.text}</label>
      </div>

      {apiError ? (
        <div>
          <h3 style={{ color: 'red' }}>Error: Unable to fetch premiums</h3>
        </div>
      ) : premiumInvalid ? (
        <div className="invalid-feedback premiumInvalid d-block">
          <h4>{t('the_premium_value_is_invalid')}</h4>
          {t('try_other_options_or_contact_support')}
        </div>
      ) : premiumLessThanTwelve ? (
        <div className="invalid-feedback premiumLessThanTwelve d-block">
          <h4>{t('the_monthly_premium_is_bellow_the_minimum_allowed')}</h4>
          {t('the_premium_could_reach_the_minimum_allowed')}
        </div>
      ) : (
        <div className="d-flex justify-content-center">
          <div className="amount">
            <div className="align-middle">
              {question.possibleValues[0] && renderRadioButton(0)}
            </div>

            {shouldShowBothButtons && (
              <div className="or align-middle">{t('or')}</div>
            )}

            <div className="align-middle">
              {shouldShowBothButtons && question.possibleValues[1] && renderRadioButton(1)}
            </div>
          </div>
        </div>
      )}


      <div className={`invalid-feedback ${inputRefs.current[question.uniqueId] ? 'd-block' : ''}`}>
        {validationErrors?.some((err) => err.id === question.id) && (
          <div className="invalid-feedback d-block">
            {
          validationErrors.find(
            (error) =>
              error.id === question.id &&
              error.additionalInfoId === null
          )?.errorText
        }
      </div>
    )}
      </div>

    </div>
  );

};

export default Premium;
